import { FC } from 'react';
import { Box, Grid, styled, Typography, useTheme } from '@mui/material';
import FormField from 'further-ui/components/FormField';
import { VehicleApplication } from '../types';
import { Vehicle } from 'further-types/lp/vehicle';
import { Controller, useForm } from 'react-hook-form';
import { onboardingAdmin } from 'further-ui/validations';
import { zodResolver } from '@hookform/resolvers/zod';
import NumberInput from 'further-ui/components/NumberInput';
import DatePicker from 'further-ui/components/DatePicker';
import BasicSelect from 'further-ui/components/BasicSelect';
import CurrencySelector from 'further-ui/components/CurrencySelector';
import { SpecialPartner } from 'further-types/lp/investor';
import ConfirmDialog from 'further-ui/components/ConfirmDialog';
import useFileUpload from 'hooks/ui/useFileUpload';
import FileDrop from 'further-ui/components/FileDrop';
import { UploadType } from 'further-types/files';
import { useDisclosure } from 'further-ui/hooks';
import { FileType } from 'further-ui/utils';
import { z } from 'zod';
import { Link } from 'react-router-dom';
import { createFundSetupRoute, FundSetupTab } from 'adminConstants/routes';
import useFeeTemplateOptions from 'hooks/lp/data/fee-manager/useFeeTemplateOptions';
import useCarriedInterestFeeTemplateOptions from 'hooks/lp/data/vehicle/useCarriedInterestFeeTemplateOptions';
import useEqualisationInterestFeeTemplateOptions from 'hooks/lp/data/vehicle/useEqualisationInterestFeeTemplateOptions';
import useVehicleApplication from 'hooks/lp/data/vehicleApplication/useVehicleApplication';
import { useNotification } from 'hooks/ui/useNotification';
import { getMaxSizeForUploadType } from 'further-ui/config';
import { compact, round } from 'lodash';

const Content = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'fullWidth',
})<{ fullWidth: boolean }>(({ theme, fullWidth }) => ({
  padding: 0,
  width: '100%',
  maxWidth: fullWidth ? '100%' : '600px',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(theme.spacingSizes.lg),
}));

const StyledLink = styled(Link)(({ theme }) => ({
  ...theme.typography.body2,
  textDecoration: 'underline',
  '&:hover': {
    textDecoration: 'underline',
  },
}));

const specialPartnerOptions = [
  { value: SpecialPartner.No, label: 'No' },
  { value: SpecialPartner.GeneralPartner, label: 'General partner' },
  { value: SpecialPartner.BritishBusinessBank, label: 'British Business Bank' },
];

const formSchema = onboardingAdmin.SaveCommitmentSettingsInput.omit({
  lpFeeTemplateId: true,
  carriedInterestFeeTemplateId: true,
  equalisationInterestFeeTemplateId: true,
}).extend({
  targetCommitment: z
    .number()
    .min(0.01, { message: 'The commitment amount must be greater than 0.' }),
  lpFeeTemplateId: z.string(),
  carriedInterestFeeTemplateId: z.string(),
  equalisationInterestFeeTemplateId: z.string(),
});

type FormValues = z.infer<typeof formSchema>;

type Props = {
  title: string;
  application: VehicleApplication;
  vehicle: Vehicle;
  onHideCommitmentForm: () => void;
  fullWidth?: boolean;
};

const CommitmentSettings: FC<Props> = ({
  title,
  application,
  vehicle,
  onHideCommitmentForm,
  fullWidth = false,
}) => {
  const notifyInvestorDisclosure = useDisclosure();
  const theme = useTheme();
  const { uploadFile } = useFileUpload();
  const { error: showError } = useNotification();
  const { saveCommitmentSettings, isPending } = useVehicleApplication(
    vehicle._id,
    application.id,
  );

  const { control, handleSubmit, getValues } = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      shouldCompleteCommitment: true,
      shouldNotifyInvestor: false,
      vehicleApplicationId: application.id,
      commitmentDate: application.commitmentSettings.commitmentDate,
      targetCommitment: application.targetCommitment,
      investorLocalCurrency: application.investorLocalCurrency,
      specialPartner: application.specialPartner,
      lpFeeTemplateId: application.commitmentSettings.lpFeeTemplateId ?? 'none',
      carriedInterestFeeTemplateId:
        application.commitmentSettings.carriedInterestFeeTemplateId ?? 'none',
      equalisationInterestFeeTemplateId:
        application.commitmentSettings.equalisationInterestFeeTemplateId ??
        'none',
      documents: application.commitmentSettings.documents,
    },
  });

  const lpFeeTemplates = useFeeTemplateOptions(vehicle._id, true);

  const carriedInterestFeeTemplates = useCarriedInterestFeeTemplateOptions(
    vehicle._id,
  );

  const equalisationInterestFeeTemplates =
    useEqualisationInterestFeeTemplateOptions(vehicle._id);

  const onSubmit = (data: FormValues) => {
    if (isPending) {
      return;
    }

    saveCommitmentSettings(
      {
        vehicleId: vehicle._id,
        ...data,
        lpFeeTemplateId:
          data.lpFeeTemplateId === 'none' ? undefined : data.lpFeeTemplateId,
        carriedInterestFeeTemplateId:
          data.carriedInterestFeeTemplateId === 'none'
            ? undefined
            : data.carriedInterestFeeTemplateId,
        equalisationInterestFeeTemplateId:
          data.equalisationInterestFeeTemplateId === 'none'
            ? undefined
            : data.equalisationInterestFeeTemplateId,
      },
      {
        onSuccess: onHideCommitmentForm,
      },
    );
  };

  const onBeforeSubmit = (data: FormValues) => {
    if (
      application.targetCommitment &&
      data.targetCommitment !== application.targetCommitment
    ) {
      notifyInvestorDisclosure.onOpen();
    } else {
      onSubmit(data);
    }
  };

  const desktopGridSize = fullWidth ? 4 : 6;

  return (
    <>
      <form
        onSubmit={handleSubmit(onBeforeSubmit)}
        id="commitment-settings-form"
      >
        <Content fullWidth={fullWidth}>
          <Typography variant="h5">{title}</Typography>
          <Grid
            container
            spacing={[theme.spacingSizes.sm, theme.spacingSizes.lg]}
          >
            <Grid size={{ xs: 12, md: desktopGridSize }}>
              <FormField
                label="Subscription date"
                tooltip="This is the date the investor signed their fund documents and/or subscription agreement. This is not their close date, which is set through the Equalise function."
              >
                <Controller
                  control={control}
                  name="commitmentDate"
                  render={({ field, fieldState: { error } }) => (
                    <DatePicker
                      fullWidth
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Select date"
                      disabled={application.hasClosedCommitment}
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
              </FormField>
            </Grid>
            <Grid size={{ xs: 12, md: desktopGridSize }}>
              <FormField label="Total commitment">
                <Controller
                  control={control}
                  name="targetCommitment"
                  render={({ field, fieldState: { error } }) => (
                    <NumberInput
                      fullWidth
                      onChange={field.onChange}
                      value={field.value}
                      size="small"
                      currency={vehicle.baseCurrency}
                      disabled={application.hasClosedCommitment}
                      error={!!error}
                      helperText={error?.message}
                      decimals={2}
                      hideZeroValue
                    />
                  )}
                />
              </FormField>
            </Grid>
            <Grid size={{ xs: 12, md: desktopGridSize }}>
              <FormField label="Investor local currency">
                <Controller
                  control={control}
                  name="investorLocalCurrency"
                  render={({ field, fieldState: { error } }) => (
                    <CurrencySelector
                      placeholder="Select currency"
                      value={field.value}
                      onChange={field.onChange}
                      error={error}
                    />
                  )}
                />
              </FormField>
            </Grid>
            <Grid size={{ xs: 12, md: desktopGridSize }}>
              <FormField label="Special partner">
                <Controller
                  control={control}
                  name="specialPartner"
                  render={({ field, fieldState: { error } }) => (
                    <BasicSelect
                      fullWidth
                      size="small"
                      placeholder="Select special partner type"
                      value={field.value}
                      onChange={field.onChange}
                      options={specialPartnerOptions}
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
              </FormField>
            </Grid>
            <Grid size={{ xs: 12, md: desktopGridSize }}>
              <FormField label="Equalisation interest fee template">
                <Controller
                  control={control}
                  name="equalisationInterestFeeTemplateId"
                  render={({ field, fieldState: { error } }) => (
                    <BasicSelect
                      fullWidth
                      size="small"
                      placeholder="Select template"
                      value={field.value}
                      onChange={field.onChange}
                      options={equalisationInterestFeeTemplates}
                      error={!!error}
                      helperText={error?.message}
                      noOptionsMessage="No fee templates available"
                    />
                  )}
                />
              </FormField>
            </Grid>
            <Grid size={{ xs: 12, md: desktopGridSize }}>
              <FormField label="Management fee template">
                <Controller
                  control={control}
                  name="lpFeeTemplateId"
                  render={({ field, fieldState: { error } }) => (
                    <BasicSelect
                      fullWidth
                      size="small"
                      placeholder="Select template"
                      value={field.value}
                      onChange={field.onChange}
                      options={lpFeeTemplates}
                      error={!!error}
                      helperText={error?.message}
                      noOptionsMessage="No fee templates available"
                    />
                  )}
                />
              </FormField>
            </Grid>
            <Grid size={{ xs: 12, md: desktopGridSize }}>
              <FormField label="Carried interest template">
                <Controller
                  control={control}
                  name="carriedInterestFeeTemplateId"
                  render={({ field, fieldState: { error } }) => (
                    <BasicSelect
                      fullWidth
                      size="small"
                      placeholder="Select template"
                      value={field.value}
                      onChange={field.onChange}
                      options={carriedInterestFeeTemplates}
                      error={!!error}
                      helperText={error?.message}
                      noOptionsMessage="No fee templates available"
                    />
                  )}
                />
              </FormField>
            </Grid>
          </Grid>

          {application.hasClosedCommitment ? (
            <Typography variant="body2" color="text.secondary">
              This investor has already completed their subscription.
            </Typography>
          ) : null}
          <Typography variant="body2">
            To add new fee templates, visit{' '}
            <StyledLink
              to={createFundSetupRoute(FundSetupTab.FeeSettings)}
              target="_blank"
            >
              Core fee templates
            </StyledLink>{' '}
            or{' '}
            <StyledLink
              to={createFundSetupRoute(FundSetupTab.CarrySettings)}
              target="_blank"
            >
              Carried interest templates
            </StyledLink>
            .
          </Typography>
          <Controller
            name="documents"
            control={control}
            render={({ field: { value, onChange }, fieldState: { error } }) => {
              return (
                <FileDrop
                  inputTitle="Upload subscription documents"
                  maxFiles={10}
                  files={value.map((document) => ({
                    fileName: document.name,
                    previewUrl: document.path,
                    isUploading: !document.path,
                  }))}
                  onDeleteFile={(index) => {
                    onChange(value.filter((_, i) => i !== index));
                  }}
                  onDropFile={async (files) => {
                    const handleFileUpload = async (file: File) => {
                      const { data, error } = await uploadFile(
                        file,
                        UploadType.CommitmentDocuments,
                      );

                      if (error) {
                        showError(error.message);
                        return null;
                      }

                      return {
                        name: data.name,
                        path: data.filePath,
                      };
                    };

                    onChange([
                      ...value,
                      ...files.map((file) => ({
                        name: file.fileName,
                        path: '',
                      })),
                    ]);

                    const uploadedFiles = compact(
                      await Promise.all(
                        files.map(
                          async (droppedFile) =>
                            await handleFileUpload(droppedFile.fileData),
                        ),
                      ),
                    );

                    onChange([...value, ...uploadedFiles]);
                  }}
                  sizeRestriction={{
                    unit: 'mb',
                    value: round(
                      getMaxSizeForUploadType(
                        UploadType.CommitmentDocuments,
                        'mb',
                      ),
                    ),
                  }}
                  supportedFormats={[FileType.PDF]}
                  error={!!error?.message}
                  helperText={error?.message}
                />
              );
            }}
          />
        </Content>
      </form>
      <ConfirmDialog
        maxWidth="sm"
        open={notifyInvestorDisclosure.isOpen}
        onClose={notifyInvestorDisclosure.onClose}
        title="Commitment amount changed"
        content="As the commitment amount has changed, you can send a system email to the investor confirming this change. Would you like to notify the investor of the change?"
        actions={{
          alternateConfirm: {
            label: 'Save only',
            variant: 'contained',
            onClick: () => {
              notifyInvestorDisclosure.onClose();
              onSubmit({ ...getValues(), shouldNotifyInvestor: false });
            },
          },
          confirm: {
            label: 'Save and notify',
            onClick: () => {
              notifyInvestorDisclosure.onClose();
              onSubmit({ ...getValues(), shouldNotifyInvestor: true });
            },
          },
        }}
      />
    </>
  );
};

export default CommitmentSettings;
